import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { saveContasAPagar } from '../../../services/ContasAPagarService';
import SwitchInput from '../../../components/SwitchInput/SwitchInput';
import SelectCentroDeCusto from './SelectCentroDeCusto';

function ContasAPagarModal({ data, onSubmit }) {
    const initialState = {
        custo_id: '',
        nome: '',
        descricao: '',
        vlr_pagar: '',
        data_vencimento: '',
        isActive: false,
        isParcelado: false,
        qtd_parcelas: '',
    };

    function detectarEFormatarData(dataString, formatoDestino) {
        const formatos = [
            { regex: /^(\d{4})-(\d{2})-(\d{2})$/, formato: 'YYYY-MM-DD' },
            { regex: /^(\d{2})\/(\d{2})\/(\d{4})$/, formato: 'DD/MM/YYYY' },
            { regex: /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/, formato: 'YYYY-MM-DD HH:mm' }
        ];

        let data;
        let formatoOrigem;

        // Tentar detectar o formato
        for (let i = 0; i < formatos.length; i++) {
            if (dataString.match(formatos[i].regex)) {
                formatoOrigem = formatos[i].formato;
                data = new Date(dataString);
                break;
            }
        }

        if (!data || isNaN(data.getTime())) {
            return 'Data inválida ou formato não suportado!';
        }

        // Função auxiliar para formatar a data
        function formatarData(data, formato) {
            const pad = (num) => num.toString().padStart(2, '0');
            const ano = data.getFullYear();
            const mes = pad(data.getMonth() + 1);
            const dia = pad(data.getDate());
            const hora = pad(data.getHours());
            const minuto = pad(data.getMinutes());

            return formato.replace('YYYY', ano).replace('MM', mes).replace('DD', dia)
                .replace('HH', hora).replace('mm', minuto);
        }

        // Formatar data para o novo formato
        return formatarData(data, formatoDestino);
    }

    // Exemplos de uso
    console.log("----------------------------------------------------------------");
    console.log(detectarEFormatarData('2024-05-03', 'DD/MM/YYYY')); // "03/05/2024"
    console.log(detectarEFormatarData('03/05/2024', 'YYYY-MM-DD')); // "2024-05-03"
    console.log(detectarEFormatarData('2024-05-03 14:00', 'DD/MM/YYYY HH:mm')); // "03/05/2024 14:00"
    console.log("----------------------------------------------------------------");
    const [contasAPagar, setContasAPagar] = useState({ ...initialState, ...data });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const btnClose = useRef(null);

    useEffect(() => {
        setContasAPagar(prevState => ({ ...prevState, ...data }));
    }, [data]);

    const handleInputChange = (event) => {
        const { id, value, type, checked } = event.target;
        setContasAPagar(prevState => ({
            ...prevState,
            [id]: type === 'checkbox' ? checked : value
        }));
    };

    const formatDate = (dateString) => {
        // Dividindo a data em partes para evitar conversão automática para UTC
        const parts = dateString.split('-');
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Ajuste porque meses são de 0 a 11
        const day = parseInt(parts[2], 10);

        // Criando uma data no horário local
        const localDate = new Date(year, month, day);

        // Opções para formatar a data no estilo brasileiro
        // const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };

        // Formatação para o padrão brasileiro
        return localDate.toLocaleDateString('pt-BR', options);
    };


    const formatCurrency = (value) => {
        value = value.replace(/[^\d]/g, '');
        if (value.length < 3) return value;
        return `${value.slice(0, -2)},${value.slice(-2)}`;
    };

    const handleSubmit = () => {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
            setError('Authentication token is missing.');
            setIsLoading(false);
            return;
        }
        saveContasAPagar(contasAPagar.id, contasAPagar, token)
            .then(result => {
                if (result) {
                    onSubmit(result);
                    setContasAPagar({ ...initialState }); // Reset the form state
                    btnClose.current.click();
                } else {
                    setError('Unable to update settings.');
                }
            })
            .catch(err => {
                setError(err.message || 'An error occurred.');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <div className="modal fade" id="modalContasAPagar" tabIndex="-1" role="dialog" aria-labelledby="modalTitleNotify" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title" id="modalTitleNotify">{contasAPagar.id ? 'Alterando ' : 'Nova '}conta a pagar</h5>
                        <button ref={btnClose} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <form onSubmit={e => e.preventDefault()}>
                            <div className='row'>
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="custo_id">Tipo do ponto</label>
                                        <SelectCentroDeCusto id={contasAPagar.custo_id} onChange={handleInputChange} />
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="nome">Descrição:</label>
                                        <input type="text" id="nome" className="form-control" onChange={handleInputChange} value={contasAPagar.nome} placeholder="Descrição da conta" />
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="descricao">Observação:</label>
                                        <input type="text" id="descricao" className="form-control" onChange={handleInputChange} value={contasAPagar.descricao} placeholder="Observação da conta" />
                                    </div>
                                </div>
                            </div>

                            <SwitchInput id="isParcelado" text="Conta parcelada?" onChange={handleInputChange} isChecked={contasAPagar.isParcelado} />
                            <div className="row">
                                {contasAPagar.isParcelado && (

                                    <div className="col-4 mb-3">
                                        <div className="form-group">
                                            <label htmlFor="qtd_parcelas">Parcelas:</label>
                                            <input type="text" id="qtd_parcelas" className="form-control" onChange={handleInputChange} value={contasAPagar.qtd_parcelas || ''} placeholder="Quantidade de Parcelas" />
                                        </div>
                                    </div>


                                )}


                                <div className="col-4 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="vlr_pagar">Valor:</label>
                                        <input type="text" id="vlr_pagar" className="form-control" onChange={handleInputChange} value={formatCurrency(contasAPagar.vlr_pagar)} placeholder="Valor a pagar" />
                                    </div>
                                </div>

                                <div className="col-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="data_vencimento">Vencimento:</label>
                                        <input type="text" id="data_vencimento" className="form-control" onChange={handleInputChange} value={(contasAPagar.data_vencimento)} placeholder="Data de Vencimento (DD/MM/AAAA)" />
                                        {/* <input type="text" id="data_vencimento" className="form-control" onChange={handleInputChange} value="2024-05-03" placeholder="Data de Vencimento (DD/MM/AAAA)" /> */}
                                    </div>
                                </div>
                            </div>

                            <SwitchInput id="isActive" text="Ativo?" onChange={handleInputChange} isChecked={contasAPagar.isActive} />
                        </form>
                    </div>
                    <div className="modal-footer">
                        {error && <div className="alert alert-danger">{error}</div>}
                        <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={isLoading}>
                            {isLoading ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

ContasAPagarModal.propTypes = {
    data: PropTypes.object,
    onSubmit: PropTypes.func
};

export default ContasAPagarModal;
